import { useDialog } from '@globals/dialog/useDialogStore'
import theme, { colors } from '@globals/themes/defaultTheme'
import { Recognition } from '@kudos/http-client'
import Box from '@mui/material/Box'
import { SxProps } from '@mui/material/styles'
import BottomDrawer from '@shared/dialog/BottomDrawer'
import { mobileFilterDrawerMaxWidth } from '@shared/filters/styles'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import useQualities from '@shared/hooks/useQualities'
import useSendRecognitionForm, {
  RecognitionFormData,
} from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { useSendRecognitionFormStore } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionFormStore'
import useDraftRecognitionsCount from '@tenant/recognition/RecognitionModal/useDraftRecognitionCount'
import { T } from '@transifex/react'
import { isEmpty } from 'lodash'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import MobileRecognitionDraftView from './DraftAndScheduleView/MobileRecognitionDraftView'
import MobileRecognitionPointsExceptionView from './MobileRecognitionPointsExceptionView'
import MobileRecognitionPointsView from './MobileRecognitionPointsView'
import MobileRecognitionQualitiesView from './MobileRecognitionQualitiesView'
import MobileRecognitionRecipientsView from './MobileRecognitionRecipientsView'
import MobileRecognitionScheduleView from './MobileRecognitionScheduleView'
import MobileRecognitionVisibilityView from './MobileRecognitionVisibilityView'
import MobileRecognitionSendDialog, {
  MOBILE_SEND_RECOGNITION_DRAWER_KEY,
} from './SendView/MobileRecognitionSendDialog'
import useMobileRecognitionDrawer, {
  MOBILE_RECOGNITION_DRAWER_KEY,
  MobileRecognitionDrawerView,
} from './useMobileRecognitionDrawer'
import useMobileRecognitionViewActions from './useMobileRecognitionViewActions'

export const MOBILE_RECOGNITION_FORM_ID = 'mobileRecognitionForm'

const MOBILE_SEND_UNSAVED_CONFIRM_DIALOG_KEY = 'mobileSendRecognitionUnsavedConfirm'

export const mobileActionStyle: SxProps = {
  position: 'fixed',
  bottom: 16,
  left: 16,
  right: 16,
  display: 'flex',
  justifyContent: 'flex-start',
  zIndex: 900, // keep under the onboarding FAB (zIndex 1000)
}

const noopShowAnimation = (_show: boolean) => {
  /* no-op for mobile: animation is not used */
}

export const DEFAULT_TITLE = <T _str="Recognize someone" />

const viewTitles: Record<MobileRecognitionDrawerView, ReactNode> = {
  [MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED]: DEFAULT_TITLE,
  [MobileRecognitionDrawerView.RECIPIENTS]: DEFAULT_TITLE,
  [MobileRecognitionDrawerView.VISIBILITY]: <T _str="Set post visibility" />,
  [MobileRecognitionDrawerView.QUALITIES]: <T _str="Add qualities" />,
  [MobileRecognitionDrawerView.POINTS]: <T _str="Add points" />,
  [MobileRecognitionDrawerView.SCHEDULE]: <T _str="Schedule post" />,
  [MobileRecognitionDrawerView.POINTS_EXCEPTION]: <T _str="Point exceptions" />,
}

const MobileRecognitionDrawer = () => {
  const { closeDrawer, dialogData, isOpen, setView, openDrawer } = useMobileRecognitionDrawer()
  const { isOpen: isSendDialogOpen, closeDialog: closeSendRecognitionDialog } = useDialog(
    MOBILE_SEND_RECOGNITION_DRAWER_KEY,
  )
  const { draftRecognitionCount } = useDraftRecognitionsCount({ enabled: isOpen })
  const { qualities } = useQualities()
  const { setIsFormDirty } = useSendRecognitionFormStore()
  const currentView = dialogData?.view ?? MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED
  const recognitionFromDrawer = dialogData?.recognition

  // Preserve recognition when closing the drawer to open the send dialog so the form
  // is not reset (which would clear sendTo) before the send dialog is shown.
  const recognitionRef = useRef<Recognition | undefined>(undefined)
  if (recognitionFromDrawer !== undefined) {
    recognitionRef.current = recognitionFromDrawer
  }
  const recognition =
    recognitionFromDrawer ?? (isSendDialogOpen ? recognitionRef.current : undefined)

  const onSuccess = () => {
    closeSendRecognitionDialog()
    closeDrawer()
  }

  const {
    form,
    isFormDirty,
    isEditMode,
    pointsExceptions,
    publishRecognition,
    handleSaveAsDraft,
    handleScheduleRecognition,
    handleResetRecognitionForm,
    isPendingCreateOrSave,
  } = useSendRecognitionForm(noopShowAnimation, !isEmpty(qualities), recognition, onSuccess)

  const viewContent: Record<MobileRecognitionDrawerView, ReactNode> = useMemo(() => {
    return {
      [MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED]: <MobileRecognitionDraftView />,
      [MobileRecognitionDrawerView.RECIPIENTS]: <MobileRecognitionRecipientsView />,
      [MobileRecognitionDrawerView.VISIBILITY]: <MobileRecognitionVisibilityView />,
      [MobileRecognitionDrawerView.QUALITIES]: (
        <MobileRecognitionQualitiesView options={qualities} />
      ),
      [MobileRecognitionDrawerView.POINTS]: <MobileRecognitionPointsView />,
      [MobileRecognitionDrawerView.SCHEDULE]: <MobileRecognitionScheduleView />,
      [MobileRecognitionDrawerView.POINTS_EXCEPTION]: (
        <MobileRecognitionPointsExceptionView pointsExceptions={pointsExceptions} />
      ),
    }
  }, [qualities, pointsExceptions])

  const viewActions = useMobileRecognitionViewActions(form)

  const submitForm = async (data: RecognitionFormData) => {
    return publishRecognition(data, !!pointsExceptions, handleResetRecognitionForm)
  }

  useEffect(() => {
    setIsFormDirty(isFormDirty)
  }, [isFormDirty, setIsFormDirty])

  useEffect(() => {
    if (pointsExceptions) {
      setView(MobileRecognitionDrawerView.POINTS_EXCEPTION)
      openDrawer({ view: MobileRecognitionDrawerView.POINTS_EXCEPTION })
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsExceptions])

  useEffect(() => {
    if (!isSendDialogOpen) {
      recognitionRef.current = undefined
    }
  }, [isSendDialogOpen])

  useEffect(() => {
    if (dialogData?.qualities && dialogData.qualities.length > 0) {
      form.setValue('qualities', dialogData.qualities, { shouldValidate: true })
    }
    if (dialogData?.recipients && dialogData.recipients.length > 0) {
      form.setValue('sendTo', dialogData.recipients, { shouldValidate: true })
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogData?.qualities, dialogData?.recipients])

  const effectiveView = useMemo(() => {
    if (
      currentView === MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED &&
      !(draftRecognitionCount?.total && draftRecognitionCount.total > 0)
    ) {
      return MobileRecognitionDrawerView.RECIPIENTS
    }
    return currentView
  }, [currentView, draftRecognitionCount?.total])

  // Keep the last view while closing so the drawer doesn't flash to different content/height during exit animation (dialogData is cleared on close).
  const lastOpenViewRef = useRef(effectiveView)
  if (isOpen) {
    lastOpenViewRef.current = effectiveView
  }
  const displayView = isOpen ? effectiveView : lastOpenViewRef.current

  const actionContent = viewActions[displayView]

  const shouldUseModalZIndex = displayView !== MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED
  const hideCloseButton = [
    MobileRecognitionDrawerView.QUALITIES,
    MobileRecognitionDrawerView.POINTS,
    MobileRecognitionDrawerView.SCHEDULE,
    MobileRecognitionDrawerView.POINTS_EXCEPTION,
    MobileRecognitionDrawerView.VISIBILITY,
  ].includes(displayView)

  const attemptCloseRef = useRef<((doClose: () => void) => boolean) | null>(null)

  return (
    <FormContextWrapper
      formMethods={form}
      onSubmit={submitForm}
      unsavedDialogKey={MOBILE_SEND_UNSAVED_CONFIRM_DIALOG_KEY}
      onRegisterCloseHandler={attemptClose => {
        attemptCloseRef.current = attemptClose
      }}
      enableNavBlocker={false}
      formId={MOBILE_RECOGNITION_FORM_ID}>
      <BottomDrawer
        drawerKey={MOBILE_RECOGNITION_DRAWER_KEY}
        title={viewTitles[displayView]}
        backgroundColor={colors.grayUltraLight}
        onClose={() => handleResetRecognitionForm()}
        hideCloseButton={hideCloseButton}
        drawerStyles={shouldUseModalZIndex ? { zIndex: theme.zIndex.modal + 1 } : undefined}
        actionButtonCount={1}
        disableBackdropClose={isSendDialogOpen}>
        <Box sx={{ py: 2, ...mobileFilterDrawerMaxWidth }}>{viewContent[displayView]}</Box>
        {actionContent && <Box sx={mobileActionStyle}>{actionContent}</Box>}
      </BottomDrawer>
      <MobileRecognitionSendDialog
        formId={MOBILE_RECOGNITION_FORM_ID}
        handleResetRecognitionForm={handleResetRecognitionForm}
        hasQualities={!!qualities && qualities.length > 0}
        form={form}
        handleSaveAsDraft={handleSaveAsDraft}
        handleScheduleRecognition={handleScheduleRecognition}
        isPendingCreateOrSave={isPendingCreateOrSave}
        isEditMode={isEditMode}
        onBeforeClose={doClose => {
          if (attemptCloseRef.current) {
            return attemptCloseRef.current(doClose)
          }
          doClose()
          return true
        }}
      />
    </FormContextWrapper>
  )
}

export default MobileRecognitionDrawer
